/**
 * Pure math behind the "Cobro de varias cuotas" form (SHOW_PAYMENT_GROUPS):
 * one amount received (a bank transfer) split across several dues. Mirrors the
 * rules services/paymentGroups.js enforces on the backend, so the form can
 * tell the user before submitting.
 */

const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;
const EPSILON = 0.005;

/** A due that can still receive money: not paid, counted, with an open balance. */
export const isOpenDue = (due) =>
	Boolean(due) && !due.paid && due.billingStatus !== 'pendingCount' && Number(due.unpaidAmount) > 0;

export const openDues = (rows = []) => rows.filter(isOpenDue);

/** allocations: [{ debtDue, amount }] — amount may still be the raw input string. */
export const sumAllocations = (allocations = []) =>
	round2(allocations.reduce((acc, allocation) => acc + (Number(allocation?.amount) || 0), 0));

/**
 * `amount` is what the bank received; empty means "exactly what was allocated".
 * unallocated < 0 means the dues add up to more than the money received.
 */
export const allocationSummary = ({ allocations = [], amount } = {}) => {
	const allocated = sumAllocations(allocations);
	const hasAmount = amount !== '' && amount !== null && amount !== undefined;
	const received = hasAmount ? round2(Number(amount) || 0) : allocated;
	const unallocated = round2(received - allocated);
	return { allocated, received, unallocated, overAllocated: unallocated < -EPSILON };
};

/**
 * Validation messages (Spanish, shown as-is). `dues` are the open dues, used to
 * cap each share at the due's open balance.
 */
export const allocationErrors = ({ allocations = [], dues = [], amount, date } = {}) => {
	const errors = [];
	if (!date) errors.push('Complete la fecha');
	if (!allocations.length) errors.push('Seleccione al menos una cuota');
	const dueById = new Map(dues.map((due) => [String(due._id), due]));
	allocations.forEach((allocation) => {
		const due = dueById.get(String(allocation.debtDue));
		const label = due?.customId || 'Cuota';
		const value = Number(allocation.amount);
		if (!(value > 0)) {
			errors.push(`${label}: indique un monto mayor a cero`);
		} else if (due && value > Number(due.unpaidAmount) + EPSILON) {
			errors.push(`${label}: el monto supera el saldo (${due.unpaidAmount})`);
		}
	});
	const hasAmount = amount !== '' && amount !== null && amount !== undefined;
	if (hasAmount && !(Number(amount) > 0)) errors.push('Monto recibido: indique un monto mayor a cero');
	const { overAllocated, allocated, received } = allocationSummary({ allocations, amount });
	if (hasAmount && overAllocated) errors.push(`Las cuotas suman ${allocated} y el cobro es de ${received}`);
	return errors;
};

/** Body for POST /api/payment-groups. An empty `amount` is omitted (backend defaults to the allocated sum). */
export const buildPaymentGroupPayload = ({ date, amount, reference, paymentMethod, observations, allocations = [] }) => {
	const payload = {
		date,
		reference: reference || undefined,
		paymentMethod: paymentMethod || undefined,
		observations: observations || undefined,
		allocations: allocations.map((allocation) => ({ debtDue: allocation.debtDue, amount: round2(allocation.amount) })),
	};
	if (amount !== '' && amount !== null && amount !== undefined) payload.amount = round2(amount);
	return payload;
};
